import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { insightsApi, assetsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import AssetCombobox from '../components/assets/AssetCombobox';
import { Button } from '../components/ui/button';
import { Loader2, Package } from 'lucide-react';
import { toast } from 'sonner';
import {
  RC, StatCard, ChartEmpty, Panel, ColumnChart, RankBars,
} from './reports/reportKit';
import { Segmented } from '../components/ui/segmented';

const fmtMoney = (val) => `$${(val || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const monthShort = (m) => {
  try { return new Date(`${m}-01T00:00:00`).toLocaleDateString('en-AU', { month: 'short' }); }
  catch { return m; }
};

// Band → colour tokens (health & on-time)
const BAND = {
  healthy: { text: '#047857', bg: '#ECFDF5', border: '#A7F3D0', bar: '#10B981' },
  attention: { text: '#8A6D00', bg: '#FFF8D6', border: '#EFE0A0', bar: '#E0B800' },
  risk: { text: '#B91C1C', bg: '#FEF2F2', border: '#FECACA', bar: '#EF4444' },
  neutral: { text: '#6B6660', bg: '#F2F0EA', border: '#E2DED4', bar: '#B5AFA3' },
};
const scoreBand = (s) => (s >= 80 ? 'healthy' : s >= 60 ? 'attention' : 'risk');

// Compliance / cert state → tone
const STATE_TONE = {
  valid: 'healthy', ok: 'healthy',
  expiring: 'attention', missing: 'attention',
  expired: 'risk', overdue: 'risk',
};
const STATE_LABEL = {
  valid: 'Valid', ok: 'Scheduled', expiring: 'Expiring',
  missing: 'Missing', expired: 'Expired', overdue: 'Overdue',
};

const AssetStatusPill = ({ status }) => {
  const map = {
    active: { t: '#047857', b: '#ECFDF5', bd: '#A7F3D0', l: 'Active' },
    in_maintenance: { t: '#8A6D00', b: '#FFF8D6', bd: '#EFE0A0', l: 'In maintenance' },
    out_of_service: { t: '#B91C1C', b: '#FEF2F2', bd: '#FECACA', l: 'Out of service' },
  };
  const m = map[status] || { t: '#6B6660', b: '#F2F0EA', bd: '#E2DED4', l: status || '—' };
  return (
    <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full border" style={{ color: m.t, background: m.b, borderColor: m.bd }}>
      {m.l}
    </span>
  );
};

// ─── Health score card (Fix 1) ──────────────────────────────────────
const HealthCard = ({ health }) => {
  const b = BAND[health.band.level] || BAND.neutral;
  return (
    <div className="bg-white rounded-xl border border-[#E6E2D9] p-5 h-full" data-testid="insight-health-card">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.09em] text-[#8A857A]">Health score</p>
          <p className="mt-1 font-mono font-bold leading-none" style={{ fontSize: 44, color: b.text }} data-testid="insight-health-score">{health.score}</p>
        </div>
        <span className="text-[13px] font-semibold px-3 py-1 rounded-full border" style={{ color: b.text, background: b.bg, borderColor: b.border }} data-testid="insight-health-band">
          {health.band.label}
        </span>
      </div>
      <div className="h-2 rounded-[4px] mt-3 overflow-hidden" style={{ background: RC.track }}>
        <div style={{ width: `${health.score}%`, height: '100%', background: b.bar }} />
      </div>
      <div className="grid gap-[9px] mt-4">
        {health.factors.map((f, i) => (
          <div key={i} className="flex items-center justify-between">
            <span className="text-[12.5px] text-[#3D3A35]">{f.label}</span>
            <div className="flex items-center gap-3">
              <span className="font-mono text-[12px] text-[#8F8A7F]">{f.value}</span>
              <span className="font-mono text-[12px] font-semibold w-9 text-right" style={{ color: f.deduction > 0 ? '#B91C1C' : '#B5AFA3' }}>
                {f.deduction > 0 ? `−${f.deduction}` : '0'}
              </span>
            </div>
          </div>
        ))}
      </div>
      <p className="text-[12.5px] leading-relaxed text-[#6B6660] mt-4 pt-3 border-t border-[#F0EDE6]">{health.note}</p>
    </div>
  );
};

// ─── Cost breakdown rows (Fix 2.2 — zero rows grey, no bar) ──────────
const CostRow = ({ label, value, total, color }) => {
  const zero = !value || value <= 0;
  return (
    <div className="py-1.5">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[12.5px] text-[#6B6660]">{label}</span>
        <span className="font-mono text-[12.5px]" style={{ color: zero ? '#B5AFA3' : '#1A1A1A' }}>{fmtMoney(value)}</span>
      </div>
      {!zero && (
        <div className="h-2 rounded-[4px] overflow-hidden" style={{ background: RC.track }}>
          <div style={{ width: `${total > 0 ? (value / total) * 100 : 0}%`, height: '100%', background: color }} />
        </div>
      )}
    </div>
  );
};

// ─── Compliance card (Fix 3) ─────────────────────────────────────────
const ComplianceCard = ({ compliance, onOpen }) => {
  const v = BAND[compliance.verdict.tone] || BAND.neutral;
  return (
    <div className="bg-white rounded-xl border border-[#E6E2D9] overflow-hidden" data-testid="insight-compliance-card">
      <div className="flex items-center justify-between px-4 pt-4 pb-3">
        <div>
          <span className="text-[15px] font-bold text-[#1A1A1A]" style={{ fontFamily: 'Manrope, sans-serif' }}>Compliance</span>
          <span className="text-[12.5px] text-[#8F8A7F] ml-2">Certificates, inspections and standards</span>
        </div>
        <span className="text-[12px] font-semibold px-2.5 py-1 rounded-full border" style={{ color: v.text, background: v.bg, borderColor: v.border }} data-testid="insight-compliance-verdict">
          {compliance.verdict.label}
        </span>
      </div>
      <div className="px-4">
        {compliance.rows.length === 0 ? (
          <ChartEmpty line2="Link a standard to this asset to track its certificates and inspections." testId="insight-compliance-empty" />
        ) : (
          compliance.rows.map((r, i) => {
            const tone = BAND[STATE_TONE[r.state] || 'neutral'];
            return (
              <div key={i} className="flex items-center justify-between py-2.5 border-b border-[#F4F2ED] last:border-0" data-testid={`compliance-row-${i}`}>
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="w-[7px] h-[7px] rounded-full shrink-0" style={{ background: tone.bar }} />
                  <div className="min-w-0">
                    <div className="text-[13px] font-semibold text-[#1A1A1A] truncate">{r.name}</div>
                    <div className="text-[11.5px] text-[#8F8A7F] truncate">{r.meta}</div>
                  </div>
                </div>
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full border shrink-0 ml-3" style={{ color: tone.text, background: tone.bg, borderColor: tone.border }}>
                  {STATE_LABEL[r.state] || r.state}
                </span>
              </div>
            );
          })
        )}
      </div>
      <div className="flex items-center justify-between px-4 py-3 mt-1 bg-[#FBFAF7] border-t border-[#E6E2D9]">
        <span className="text-[12px] text-[#6B6660]">{compliance.standards_line}</span>
        <Button variant="outline" size="sm" className="h-[30px] border-[#C9C3B6] rounded-[7px]" onClick={onOpen} data-testid="insight-open-certificates">
          Open certificates
        </Button>
      </div>
    </div>
  );
};

// ─── Asset Insights Tab ──────────────────────────────────────────────
const AssetInsightsTab = ({ months }) => {
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [selectedAssetId, setSelectedAssetId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    assetsApi.getAll().then(res => setAssets(res.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedAssetId) { setData(null); return; }
    setLoading(true);
    insightsApi.assetInsights(selectedAssetId, months)
      .then(res => setData(res.data))
      .catch(() => toast.error('Failed to load insights'))
      .finally(() => setLoading(false));
  }, [selectedAssetId, months]);

  const t = data?.totals;
  const median = t?.fleet_median_cost_per_hour;
  const catLabel = data?.asset?.category ? data.asset.category.replace(/_/g, ' ') : '';

  return (
    <div className="flex flex-col gap-6">
      {/* Asset picker bar (Fix 5) */}
      <div className="bg-white rounded-xl border border-[#E6E2D9] px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
        <AssetCombobox
          assets={assets}
          value={selectedAssetId || null}
          onChange={(id) => setSelectedAssetId(id || '')}
          placeholder="Select an asset…"
          className="flex-1 min-w-[240px]"
          testId="insight-asset-select"
        />
        {data && (
          <div className="flex items-center gap-4 shrink-0" data-testid="insight-asset-context">
            <AssetStatusPill status={data.asset.status} />
            <span className="text-[12.5px] text-[#6B6660]">
              <span className="font-mono font-semibold text-[#1A1A1A]">{(data.asset.hours || 0).toLocaleString()}</span> hrs
            </span>
          </div>
        )}
      </div>

      {!selectedAssetId && (
        <div className="bg-white rounded-xl border border-[#E6E2D9] py-16 text-center" data-testid="insight-no-asset">
          <Package className="w-10 h-10 mx-auto mb-3 text-[#C9C3B6]" />
          <p className="text-[14px] font-semibold text-[#1A1A1A]">Select an asset to view its insights</p>
          <p className="text-[12.5px] text-[#8F8A7F] mt-1">Health score, costs and compliance for any machine in the fleet.</p>
        </div>
      )}

      {loading && <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-primary animate-spin" /></div>}

      {data && !loading && (
        <>
          {/* Headline stats (Fix 5 — 3px accent, no icon tiles) */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Cost / operating hour"
              value={t.cost_per_operating_hour > 0 ? `${fmtMoney(t.cost_per_operating_hour)}/h` : '—'}
              subtitle={median != null ? `Fleet median ${fmtMoney(median)}/h for ${catLabel}` : 'No fleet comparator yet'}
              accent={RC.volt}
              testId="insight-stat-cph"
            />
            <StatCard title="Total cost" value={t.cost > 0 ? fmtMoney(t.cost) : '—'} subtitle={`Last ${months} months`} accent={RC.success} testId="insight-stat-cost" />
            <StatCard title="Hours worked" value={`${t.hours_worked}h`} subtitle="Recorded on work orders" accent={RC.volt} testId="insight-stat-hours" />
            <StatCard title="WOs completed" value={t.work_orders_completed} subtitle={`Last ${months} months`} accent={RC.volt} testId="insight-stat-wos" />
          </div>

          {/* Health card + cost panels (Fix 1 + Fix 2) */}
          <div className="grid lg:grid-cols-2 gap-6 items-start">
            <HealthCard health={data.health} />
            <div className="flex flex-col gap-6">
              <Panel title="Cost breakdown" testId="insight-cost-breakdown">
                <CostRow label="Labour" value={t.labour} total={t.cost} color={RC.ink} />
                <CostRow label="Parts" value={t.parts} total={t.cost} color={RC.volt} />
                <CostRow label="Misc" value={t.misc} total={t.cost} color={RC.orange} />
                <div className="flex items-center justify-between pt-3 mt-1 border-t border-[#F0EDE6]">
                  <span className="text-[13px] font-semibold text-[#1A1A1A]">Total</span>
                  <span className="font-mono text-[13px] font-bold" style={{ color: t.cost > 0 ? '#1A1A1A' : '#B5AFA3' }}>{fmtMoney(t.cost)}</span>
                </div>
              </Panel>
              <Panel title="Monthly cost trend" subtitle="Labour vs parts" testId="insight-cost-trend">
                <ColumnChart
                  data={(data.monthly_trend || []).map(m => ({ label: monthShort(m.month), labour: m.labour, parts: m.parts }))}
                  xKey="label"
                  bars={[{ key: 'labour', name: 'Labour', color: RC.ink }, { key: 'parts', name: 'Parts', color: RC.volt }]}
                  emptyLine2="Costs appear once labour hours or parts are recorded against a work order."
                  testId="insight-cost-trend-chart"
                />
              </Panel>
            </div>
          </div>

          {/* Compliance (Fix 3) */}
          <ComplianceCard compliance={data.compliance} onOpen={() => navigate('/compliance')} />

          {/* Open work (Fix 3) */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard title="Open work orders" value={data.open_work.open_work_orders}
              subtitle={data.open_work.overdue_work_orders > 0 ? `${data.open_work.overdue_work_orders} overdue` : 'None overdue'}
              accent={RC.volt} testId="insight-open-wos" />
            <StatCard title="Open defects" value={data.open_work.open_defects}
              subtitle={data.open_work.open_defects > 0
                ? `${data.open_work.defect_breakdown.critical} critical · ${data.open_work.defect_breakdown.medium} medium · ${data.open_work.defect_breakdown.low} low`
                : 'None open'}
              accent={data.open_work.defect_breakdown.critical > 0 ? RC.danger : RC.warning} testId="insight-open-defects" />
            <StatCard title="Inspections overdue" value={data.open_work.inspections_overdue}
              subtitle={data.open_work.inspection_detail || 'All on schedule'}
              accent={data.open_work.inspections_overdue > 0 ? RC.danger : RC.success} testId="insight-insp-overdue" />
            <StatCard title="Expired certificates" value={data.open_work.expired_certificates}
              subtitle={data.open_work.expiring_certificates > 0 ? `${data.open_work.expiring_certificates} expiring soon` : 'All current'}
              accent={data.open_work.expired_certificates > 0 ? RC.danger : RC.success} testId="insight-expired-certs" />
          </div>
        </>
      )}
    </div>
  );
};

// ─── On-time completion (Fix 4) ──────────────────────────────────────
const OnTimePanel = ({ summary }) => {
  const pct = summary.on_time_pct;
  const level = pct >= 80 ? 'healthy' : pct >= 50 ? 'attention' : 'risk';
  const b = BAND[level];
  const total = summary.scheduled_completed || 0;
  return (
    <Panel title="On-time completion" testId="insight-ontime">
      {total === 0 ? (
        <ChartEmpty line2="No scheduled work orders were completed in this period." testId="insight-ontime-empty" />
      ) : (
        <div>
          <p className="font-mono font-bold leading-none" style={{ fontSize: 34, color: b.text }} data-testid="insight-ontime-rate">{pct}%</p>
          <p className="text-[12.5px] text-[#6B6660] mt-1">{summary.on_time_count} of {total} completed on time</p>
          <div className="h-[22px] rounded-[5px] overflow-hidden flex mt-3" style={{ background: RC.track }}>
            {summary.on_time_count > 0 && <div style={{ width: `${(summary.on_time_count / total) * 100}%`, background: '#10B981' }} />}
            {summary.late_count > 0 && <div style={{ width: `${(summary.late_count / total) * 100}%`, background: '#FF4F00' }} />}
          </div>
          <div className="flex gap-5 mt-3">
            <span className="inline-flex items-center gap-1.5 text-[12px] text-[#3D3A35]"><span className="w-[9px] h-[9px] rounded-[2px]" style={{ background: '#10B981' }} />On time <span className="font-mono font-semibold">{summary.on_time_count}</span></span>
            <span className="inline-flex items-center gap-1.5 text-[12px] text-[#3D3A35]"><span className="w-[9px] h-[9px] rounded-[2px]" style={{ background: '#FF4F00' }} />Late <span className="font-mono font-semibold">{summary.late_count}</span></span>
          </div>
        </div>
      )}
    </Panel>
  );
};

// ─── Work Order Insights Tab ─────────────────────────────────────────
const WorkOrderInsightsTab = ({ months }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    insightsApi.workOrderInsights(months)
      .then(res => setData(res.data))
      .catch(() => toast.error('Failed to load insights'))
      .finally(() => setLoading(false));
  }, [months]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-primary animate-spin" /></div>;
  if (!data) return null;

  const s = data.summary;
  const c = data.costs;

  const ageRows = (data.ageing.buckets || []).map(b => ({
    label: b.bucket, value: b.count, color: /90\+/.test(b.bucket) ? RC.danger : /31/.test(b.bucket) ? RC.orange : RC.volt,
  }));
  const typeRows = (data.by_type || []).map(t => ({
    label: t.type,
    segments: [{ value: t.open, color: RC.volt }, { value: t.completed, color: RC.success }],
    display: `${t.open + t.completed}`,
  }));

  return (
    <div className="flex flex-col gap-6">
      {/* Stat cards (Fix 5 — no icon tiles) */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard title="Total work orders" value={s.total} subtitle={`Last ${months} months`} accent={RC.volt} testId="wo-stat-total" />
        <StatCard title="Completed" value={s.completed} subtitle={s.total > 0 ? `${Math.round((s.completed / s.total) * 100)}% of total` : '—'} accent={RC.success} testId="wo-stat-completed" />
        <StatCard title="Open" value={s.open} subtitle={`${data.risk.open_no_assignee} unassigned`} accent={RC.volt} testId="wo-stat-open" />
        <StatCard title="Overdue" value={s.overdue} subtitle={s.overdue === 0 ? 'All on schedule' : 'Past due date'} accent={RC.danger} testId="wo-stat-overdue" />
        <StatCard title="Median close time" value={s.median_close_days > 0 ? `${s.median_close_days} days` : '—'} subtitle="Open to done" accent={RC.volt} testId="wo-stat-close" />
      </div>

      {/* On-time + Cost summary */}
      <div className="grid lg:grid-cols-2 gap-6">
        <OnTimePanel summary={s} />
        <Panel title="Cost summary" testId="wo-cost-summary">
          <CostRow label="Labour" value={c.labour} total={c.total} color={RC.ink} />
          <CostRow label="Parts" value={c.parts} total={c.total} color={RC.volt} />
          <CostRow label="Misc" value={c.misc} total={c.total} color={RC.orange} />
          <div className="flex items-center justify-between pt-3 mt-1 border-t border-[#F0EDE6]">
            <span className="text-[13px] font-semibold text-[#1A1A1A]">Total</span>
            <span className="font-mono text-[13px] font-bold" style={{ color: c.total > 0 ? '#1A1A1A' : '#B5AFA3' }}>{fmtMoney(c.total)}</span>
          </div>
          <div className="mt-3 pt-3 border-t border-[#F0EDE6] bg-[#FBFAF7] -mx-4 -mb-4 px-4 py-2.5 flex items-center justify-between">
            <span className="text-[12px] text-[#6B6660]">Average per work order</span>
            <span className="font-mono text-[12.5px] font-semibold" style={{ color: c.avg_per_wo > 0 ? '#1A1A1A' : '#B5AFA3' }}>{fmtMoney(c.avg_per_wo)}</span>
          </div>
        </Panel>
      </div>

      {/* Ageing + monthly trend */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Ageing of open work" subtitle="Days since raised"
          right={data.ageing.oldest ? <span className="text-[11.5px] text-[#8F8A7F]">Oldest: {data.ageing.oldest.days}d</span> : null}
          testId="wo-ageing">
          <RankBars rows={ageRows} labelWidth={90} emptyLine2="No open work orders." testId="wo-ageing-bars" />
          {data.ageing.oldest && (
            <p className="text-[12px] text-[#8F8A7F] mt-3 pt-3 border-t border-[#F0EDE6]">
              Oldest open: <span className="text-[#3D3A35] font-medium">{data.ageing.oldest.title}</span>
              {data.ageing.oldest.asset_name ? ` · ${data.ageing.oldest.asset_name}` : ''} · {data.ageing.oldest.days} days
            </p>
          )}
        </Panel>
        <Panel title="Created vs completed" subtitle="By month" testId="wo-trend">
          <ColumnChart
            data={(data.monthly_trend || []).map(m => ({ label: monthShort(m.month), created: m.created, completed: m.completed }))}
            xKey="label"
            bars={[{ key: 'created', name: 'Created', color: RC.ink }, { key: 'completed', name: 'Completed', color: RC.success }]}
            emptyLine2="No work orders were created or completed in this period."
            testId="wo-trend-chart"
          />
        </Panel>
      </div>

      {/* By type + technician throughput */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="By work type" subtitle="Open vs completed"
          right={<span className="inline-flex items-center gap-3 text-[11px] text-[#3D3A35]"><span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-[2px]" style={{ background: RC.volt }} />Open</span><span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-[2px]" style={{ background: RC.success }} />Completed</span></span>}
          testId="wo-by-type">
          <RankBars rows={typeRows} labelWidth={160} emptyLine2="No work orders in this period." testId="wo-by-type-bars" />
        </Panel>
        <Panel title="Technician throughput" subtitle="Completed & median close" testId="wo-throughput">
          {(data.technician_throughput || []).length === 0 ? (
            <ChartEmpty line2="No work orders assigned to technicians." testId="wo-throughput-empty" />
          ) : (
            <div>
              <div className="grid items-center px-2 pb-2 border-b border-[#E6E2D9] text-[10px] font-bold uppercase tracking-[0.09em] text-[#8A857A]" style={{ gridTemplateColumns: 'minmax(160px,2fr) 100px 120px' }}>
                <span>Technician</span><span className="text-center">Completed</span><span className="text-right">Median close</span>
              </div>
              {data.technician_throughput.map((tt, i) => (
                <div key={i} className="grid items-center px-2 border-b border-[#F4F2ED] last:border-0" style={{ gridTemplateColumns: 'minmax(160px,2fr) 100px 120px', padding: '9px 8px' }} data-testid={`wo-tech-${i}`}>
                  <span className="text-[13px] text-[#1A1A1A] truncate">{tt.name}</span>
                  <span className="text-center font-mono text-[12.5px] text-[#3D3A35]">{tt.completed}</span>
                  <span className="text-right font-mono text-[12.5px]" style={{ color: tt.median_close_days == null ? '#B5AFA3' : '#3D3A35' }}>
                    {tt.median_close_days == null ? '—' : `${tt.median_close_days}d`}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      {/* Most expensive work orders */}
      <Panel title="Most expensive work orders" subtitle="By total cost" testId="wo-top-cost">
        {(data.top_costly || []).length === 0 ? (
          <ChartEmpty line2="No costed work orders in this period." testId="wo-top-cost-empty" />
        ) : (
          <div>
            <div className="grid items-center px-2 pb-2 border-b border-[#E6E2D9] text-[10px] font-bold uppercase tracking-[0.09em] text-[#8A857A]" style={{ gridTemplateColumns: 'minmax(220px,2fr) minmax(140px,1fr) 100px 110px' }}>
              <span>Work order</span><span>Asset</span><span className="text-center">Days open</span><span className="text-right">Cost</span>
            </div>
            {data.top_costly.map((wo) => (
              <div key={wo.id} className="grid items-center px-2 border-b border-[#F4F2ED] last:border-0" style={{ gridTemplateColumns: 'minmax(220px,2fr) minmax(140px,1fr) 100px 110px', padding: '10px 8px' }} data-testid={`wo-cost-${wo.id}`}>
                <span className="text-[13px] text-[#1A1A1A] truncate pr-2">{wo.title}</span>
                <span className="text-[12.5px] text-[#6B6660] truncate">{wo.asset_name || '—'}</span>
                <span className="text-center font-mono text-[12.5px] text-[#3D3A35]">{wo.days_open}</span>
                <span className="text-right font-mono text-[13px] font-bold text-[#1A1A1A]">{fmtMoney(wo.total_cost)}</span>
              </div>
            ))}
            {data.top_costly_more > 0 && (
              <p className="text-[12px] text-[#8F8A7F] px-2 pt-2.5">Showing the 5 most expensive · {data.top_costly_more} more</p>
            )}
          </div>
        )}
      </Panel>
    </div>
  );
};

// ─── Main Insights Page ──────────────────────────────────────────────
export const InsightsPage = () => {
  const { isAdmin, isMechanic, isOfficeUser } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('assets');
  const [months, setMonths] = useState(6);

  if (!isAdmin() && !isMechanic() && !isOfficeUser()) {
    navigate('/dashboard');
    return null;
  }

  const PERIODS = [
    { id: 3, label: '3m' }, { id: 6, label: '6m' }, { id: 12, label: '12m' }, { id: 24, label: '24m' },
  ];

  return (
    <div className="flex flex-col gap-6 animate-fade-in" data-testid="insights-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-[20px] font-extrabold uppercase tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Insights</h1>
          <p className="text-muted-foreground">Maintenance analytics and cost intelligence</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#6B6660]">Period</span>
          <Segmented
            value={months}
            onChange={setMonths}
            testId="insights-period"
            options={PERIODS.map(p => ({ id: p.id, label: p.label, testId: `insights-period-${p.id}` }))}
          />
        </div>
      </div>

      {/* Tabs — same Segmented control as Reports */}
      <Segmented
        value={activeTab}
        onChange={setActiveTab}
        testId="insights-tabs"
        options={[
          { id: 'assets', label: 'Asset insights', testId: 'asset-insights-tab' },
          { id: 'work-orders', label: 'Work order insights', testId: 'wo-insights-tab' },
        ]}
      />

      {activeTab === 'assets' && <AssetInsightsTab months={months} />}
      {activeTab === 'work-orders' && <WorkOrderInsightsTab months={months} />}
    </div>
  );
};

export default InsightsPage;
